import { useEffect, type ReactNode } from "react";
import { playSfx } from "../sound/sfx";
import { useSession } from "../store/context";

/**
 * App-wide toast for a live social push (friend request/accept, a DM that
 * landed while its thread wasn't open, and the game-invite kinds — driven by
 * the presence socket). Rendered once at the app root — alongside the shell —
 * so it appears over any view, including a full-screen game table.
 * Auto-dismisses after a few seconds. Tapping a `game_invite` toast opens the
 * incoming-invite prompt; other kinds jump to the tab the store named
 * (`friendToastNav`: چت for a DM, دوستان otherwise).
 */
export function FriendToast(): ReactNode {
  const toast = useSession((s) => s.friendToast);
  // One soft chime per toast (friend request/accept, invites, a DM landing
  // outside its thread) — decoration only, muted by the UI-sound toggle.
  useEffect(() => {
    if (toast) playSfx("notify");
  }, [toast]);
  const toastInvite = useSession((s) => s.friendToastInvite);
  const toastNav = useSession((s) => s.friendToastNav);
  const dismiss = useSession((s) => s.dismissFriendToast);
  const openInvitePrompt = useSession((s) => s.openInvitePrompt);
  const setView = useSession((s) => s.setView);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(dismiss, 4000);
    return () => window.clearTimeout(id);
  }, [toast, dismiss]);

  if (!toast) return null;

  return (
    <button
      type="button"
      dir="rtl"
      role="alert"
      onClick={() => {
        if (toastInvite) openInvitePrompt(toastInvite);
        else setView(toastNav);
        dismiss();
      }}
      className="fixed inset-x-0 z-[60] mx-auto flex w-max max-w-[90%] cursor-pointer appearance-none items-center gap-2 rounded-full border border-gold-deep bg-surface-container-highest px-4 py-2 font-body-md text-body-md text-on-surface shadow-lg transition-transform active:scale-95"
      // Fixed overlay: clear the status bar / Dynamic Island in Bot-API-8
      // fullscreen (iOS zeroes env() there → --tg-safe-top covers it).
      // bg-pop = the app's shared overlay entrance (no abrupt mount).
      style={{
        top: "calc(16px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        animation: "bg-pop 0.3s ease",
      }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-secondary"
      >
        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
      </svg>
      <span className="truncate">{toast}</span>
    </button>
  );
}
